import { IconTypes } from '@/components/Generic'
import type { IconType } from '@/components/Generic/Icon/iconTypes'
import { isValidIban } from '@/methods/validateEditableFields'
import type {
  Answers,
  AnswerValue,
  DefaultFrom,
  DocumentRequest,
  GroupItem,
  GroupQuestion,
  LabelMap,
  LessThan,
  LookupDefault,
  NumberQuestion,
  Question,
  QuestionsScreen,
  ScalarAnswerValue,
  ScalarQuestion,
  SelectQuestion,
  SumOperand,
  UploadedDocument,
} from '@/types/dataCollection'
import { format } from '@/methods/format'

export function isTileSelect(question: SelectQuestion): boolean {
  return question.display === 'tiles'
}

export function toIconType(name: string | undefined): IconType | undefined {
  return name && (IconTypes as readonly string[]).includes(name) ? (name as IconType) : undefined
}

export function resolveLabel(label: LabelMap | undefined, language: string): string {
  if (!label) return ''
  return label[language] ?? label.en
}

export function isQuestionVisible(question: Question, resolvedAnswers: Answers): boolean {
  if (!question.showIf) return true
  const actual = resolvedAnswers[question.showIf.questionId]
  if (actual === undefined) return false
  return String(actual) === String(question.showIf.equals)
}

export function isRequired(question: ScalarQuestion): boolean {
  return question.required !== false
}

function isEmpty(value: AnswerValue | undefined): boolean {
  return value === undefined || value === '' || value === null
}

/**
 * Numeric reading of an answer: numbers as-is, numeric strings (select option values such as
 * "3", or a number field still holding its canonical input string) parsed. Anything else has
 * no numeric value.
 */
export function toNumericValue(value: AnswerValue | undefined): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function findQuestion(
  screen: QuestionsScreen,
  id: string
): ScalarQuestion | GroupQuestion | undefined {
  for (const question of screen.questions) {
    if (question.id === id) return question
    if (isGroupQuestion(question)) {
      const inner = question.questions.find((q) => q.id === id)
      if (inner) return inner
    }
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Cross-field rule: `lessThan`
// ---------------------------------------------------------------------------

/**
 * True when the question carries a `lessThan` rule and both sides currently have a numeric
 * value that violates it. Mirrors the backend: an unanswered or non-numeric side never violates.
 */
function lessThanOf(question: ScalarQuestion): LessThan | undefined {
  return question.type === 'number' || question.type === 'select' ? question.lessThan : undefined
}

export function violatesLessThan(
  question: ScalarQuestion,
  value: AnswerValue | undefined,
  scope: Answers
): boolean {
  const lessThan = lessThanOf(question)
  if (!lessThan) return false
  const actual = toNumericValue(value)
  const bound = toNumericValue(scope[lessThan.questionId])
  return actual !== undefined && bound !== undefined && actual >= bound
}

// ---------------------------------------------------------------------------
// Auto-calculated suggestions: `defaultFrom`
// ---------------------------------------------------------------------------

/** The lookup's mapped value for the current source answer, or undefined when not evaluable. */
function lookupValue(rule: LookupDefault, answers: Answers): number | undefined {
  const source = answers[rule.questionId]
  if (isEmpty(source) || Array.isArray(source)) return undefined
  return rule.mapping[String(source)] ?? rule.fallback
}

function operandHasValue(operand: SumOperand, answers: Answers): boolean {
  if (operand.kind === 'lookup') return lookupValue(operand, answers) !== undefined
  const value = answers[operand.questionId]
  if (operand.innerQuestionId) {
    const inner = operand.innerQuestionId
    return Array.isArray(value) && value.some((item) => toNumericValue(item[inner]) !== undefined)
  }
  return toNumericValue(value) !== undefined
}

function operandValue(operand: SumOperand, answers: Answers): number {
  if (operand.kind === 'lookup') return lookupValue(operand, answers) ?? 0
  const value = answers[operand.questionId]
  if (operand.innerQuestionId) {
    if (!Array.isArray(value)) return 0
    const inner = operand.innerQuestionId
    return value.reduce((sum, item) => sum + (toNumericValue(item[inner]) ?? 0), 0)
  }
  return toNumericValue(value) ?? 0
}

function defaultFromSourceIds(rule: DefaultFrom): string[] {
  return rule.kind === 'lookup' ? [rule.questionId] : rule.operands.map((o) => o.questionId)
}

/** Whether any source of the rule currently carries a value worth deriving a suggestion from. */
export function hasDefaultFromInput(rule: DefaultFrom, answers: Answers): boolean {
  if (rule.kind === 'lookup') {
    const source = answers[rule.questionId]
    return !isEmpty(source) && !Array.isArray(source)
  }
  return rule.operands.some((operand) => operandHasValue(operand, answers))
}

function roundForQuestion(question: NumberQuestion, value: number): number {
  return question.dataType === 'integer' ? Math.round(value) : Math.round(value * 100) / 100
}

/**
 * Value the question's `defaultFrom` evaluates to right now, or undefined when it cannot be
 * evaluated (no rule, lookup source unanswered, or no mapping entry and no fallback). A sum
 * always evaluates — unanswered, non-numeric or unmapped operands count as 0.
 */
export function computeDefaultFrom(question: NumberQuestion, answers: Answers): number | undefined {
  const rule = question.defaultFrom
  if (!rule) return undefined

  if (rule.kind === 'lookup') {
    const mapped = lookupValue(rule, answers)
    return mapped === undefined ? undefined : roundForQuestion(question, mapped)
  }

  const total = rule.operands.reduce((sum, operand) => sum + operandValue(operand, answers), 0)
  return roundForQuestion(question, total)
}

/**
 * Value the "apply default" button should fill in: the `defaultFrom` suggestion once its
 * sources are answered, otherwise the static `defaultValue`.
 */
export function suggestedNumberValue(
  question: NumberQuestion,
  answers: Answers
): number | undefined {
  if (question.defaultFrom && hasDefaultFromInput(question.defaultFrom, answers)) {
    const computed = computeDefaultFrom(question, answers)
    if (computed !== undefined) return computed
  }
  return question.defaultValue
}

/**
 * Numeric equality for computed targets: a field still holding its canonical input string
 * ("2050") equals the number it will be submitted as.
 */
function sameNumber(a: AnswerValue | undefined, b: number): boolean {
  return toNumericValue(a) === b
}

/**
 * Raw equality for sources. Lookup mappings are keyed by the source answer as-is, so "01" and
 * "1" are different sources even though they are numerically equal.
 */
function sameSource(a: AnswerValue | undefined, b: AnswerValue | undefined): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b)
}

type DefaultFromTarget = NumberQuestion & { defaultFrom: DefaultFrom }

function isDefaultFromTarget(question: Question): question is DefaultFromTarget {
  return question.type === 'number' && question.defaultFrom !== undefined
}

/** Ids of the screen's questions that carry a `defaultFrom` rule. */
export function defaultFromTargetIds(screen: QuestionsScreen): string[] {
  return screen.questions.filter(isDefaultFromTarget).map((question) => question.id)
}

/**
 * Ids of the screen's `defaultFrom` targets that currently hold a computed value rather than one
 * the user typed (`overridden`). Reported on submit so the value can be refreshed when its
 * sources change on another screen.
 */
export function derivedTargetIds(
  screen: QuestionsScreen,
  values: Answers,
  overridden: ReadonlySet<string>
): string[] {
  return defaultFromTargetIds(screen).filter((id) => !overridden.has(id) && !isEmpty(values[id]))
}

/**
 * Re-evaluates every `defaultFrom` suggestion whose source answers changed between `prev` and
 * `next` (both resolved across screens), cascading so that a recomputed field feeds the fields
 * that depend on it. Fields whose sources did not change keep whatever the user typed.
 * Returns only the target values that changed.
 */
export function applyDefaultFrom(screen: QuestionsScreen, prev: Answers, next: Answers): Answers {
  const updates: Answers = {}
  let current = next
  const targets = screen.questions.filter(isDefaultFromTarget)

  for (let pass = 0; pass < targets.length; pass++) {
    let changed = false
    for (const question of targets) {
      if (question.applyDefaultLabel) continue
      const sourcesChanged = defaultFromSourceIds(question.defaultFrom).some(
        (id) => !sameSource(prev[id], current[id])
      )
      if (!sourcesChanged) continue
      const computed = computeDefaultFrom(question, current)
      if (computed === undefined || sameNumber(current[question.id], computed)) continue
      current = { ...current, [question.id]: computed }
      updates[question.id] = computed
      changed = true
    }
    if (!changed) break
  }

  return updates
}

export function isGroupQuestion(question: Question): question is GroupQuestion {
  return question.type === 'group'
}

/** The screen's single `buttons` select, when it is one (the backend guarantees it is alone). */
export function buttonsSelectOf(screen: QuestionsScreen): SelectQuestion | undefined {
  const [question] = screen.questions
  return screen.questions.length === 1 &&
    question.type === 'select' &&
    question.display === 'buttons'
    ? question
    : undefined
}

/**
 * A saved scalar answer as the review screen shows it: a select's option label, a number in the
 * user's locale with its unit, anything else verbatim.
 */
export function formatAnswerForReview(
  question: ScalarQuestion,
  value: ScalarAnswerValue,
  language: string
): string {
  if (question.type === 'select') {
    const option = question.options.find((o) => o.value === String(value))
    return option ? resolveLabel(option.label, language) : String(value)
  }
  if (question.type === 'number') {
    const numeric = toNumericValue(value)
    if (numeric === undefined) return String(value)
    const isInteger = question.dataType === 'integer'
    // Show the actual submitted value without rounding. Guard against scientific notation
    // (e.g. 1e-7) which formatLocalizedNumber's digit-only parser cannot handle.
    const plain = String(numeric)
    const canonical = isInteger
      ? String(Math.round(numeric))
      : plain.includes('e')
        ? numeric
            .toFixed(20)
            .replace(/(\.\d*?)0+$/, '$1')
            .replace(/\.$/, '')
        : plain
    const unit = resolveLabel(question.unitLabel, language) || question.unit
    const formatted = formatLocalizedNumber(canonical, language, !isInteger)
    return unit ? `${formatted} ${unit}` : formatted
  }
  if (question.type === 'boolean') return value === true ? '✓' : '–'
  return String(value)
}

export const GROUP_MIN_ITEMS_DEFAULT = 0
export const GROUP_MAX_ITEMS_DEFAULT = 10

export function groupMinItems(question: GroupQuestion): number {
  return question.minItems ?? GROUP_MIN_ITEMS_DEFAULT
}

export function groupMaxItems(question: GroupQuestion): number {
  return question.maxItems ?? GROUP_MAX_ITEMS_DEFAULT
}

export function groupInitialItems(question: GroupQuestion): number {
  const min = groupMinItems(question)
  const max = groupMaxItems(question)
  const desired = Math.max(question.initialItems ?? min, min, 1)
  return Math.min(desired, max)
}

export interface ValidationTexts {
  required: string
  min: string
  max: string
  maxLength: string
  minItems: string
  maxItems: string
  iban: string
  /** Placeholders: `{label}` (referenced question's label) and `{value}` (its numeric value). */
  lessThan: string
}

const separatorsCache = new Map<string, { group: string; decimal: string }>()

function normalizeLocale(locale: string): string {
  return locale.replace(/[A-Z].*$/, '') || 'en'
}

export function getNumberSeparators(locale: string): { group: string; decimal: string } {
  const key = locale || 'en'
  const cached = separatorsCache.get(key)
  if (cached) return cached

  let separators = { group: ',', decimal: '.' }
  for (const candidate of [normalizeLocale(key), 'en']) {
    try {
      const parts = new Intl.NumberFormat(candidate).formatToParts(11111.1)
      separators = {
        group: parts.find((p) => p.type === 'group')?.value ?? ',',
        decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
      }
      break
    } catch {}
  }

  separatorsCache.set(key, separators)
  return separators
}

export function parseNumberInput(input: string, locale: string, allowDecimal: boolean): string {
  if (!allowDecimal) return input.replace(/\D/g, '')
  const { decimal } = getNumberSeparators(locale)
  let result = ''
  let hasDecimal = false
  for (const char of input) {
    if (char >= '0' && char <= '9') result += char
    else if (char === decimal && !hasDecimal) {
      result += '.'
      hasDecimal = true
    }
  }
  return result
}

export function formatLocalizedNumber(
  canonical: string,
  locale: string,
  allowDecimal: boolean
): string {
  if (!canonical) return ''
  const negative = canonical.startsWith('-')
  const abs = negative ? canonical.slice(1) : canonical
  const { group, decimal } = getNumberSeparators(locale)
  const dot = abs.indexOf('.')
  const intDigits = (dot === -1 ? abs : abs.slice(0, dot)).replace(/\D/g, '')
  const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, group)
  const formatted =
    allowDecimal && dot !== -1
      ? `${grouped}${decimal}${abs.slice(dot + 1).replace(/\D/g, '')}`
      : grouped
  return negative ? `-${formatted}` : formatted
}

export function groupFieldErrorKey(groupId: string, index: number, innerId: string): string {
  return `${groupId}.${index}.${innerId}`
}

function validateScalar(
  question: ScalarQuestion,
  value: AnswerValue | undefined,
  scope: Answers,
  texts: ValidationTexts,
  labelFor: (questionId: string) => string
): string | undefined {
  if (!isQuestionVisible(question, scope)) return undefined

  if (question.type === 'boolean') {
    if (isRequired(question) && value !== true) return texts.required
    return undefined
  }

  if (isEmpty(value)) {
    if (isRequired(question)) return texts.required
    return undefined
  }

  if (question.type === 'number') {
    const num = typeof value === 'number' ? value : Number(value)
    if (Number.isNaN(num)) return texts.required
    if (question.min !== undefined && num < question.min) {
      return format(texts.min, { min: question.min })
    }
    if (question.max !== undefined && num > question.max) {
      return format(texts.max, { max: question.max })
    }
  }

  if (question.type === 'text' && typeof value === 'string') {
    if (question.maxLength !== undefined && value.length > question.maxLength) {
      return format(texts.maxLength, { maxLength: question.maxLength })
    }
    if (question.format === 'iban' && !isValidIban(value)) {
      return texts.iban
    }
  }

  if (question.type === 'select' && typeof value === 'string') {
    if (!question.options.some((o) => o.value === value)) return texts.required
  }

  const lessThan = lessThanOf(question)
  if (lessThan && violatesLessThan(question, value, scope)) {
    // The referenced question may live on an earlier screen, whose config is not at hand:
    // fall back to its value so the message never ends in an empty label.
    const bound = String(toNumericValue(scope[lessThan.questionId]))
    return format(texts.lessThan, {
      label: labelFor(lessThan.questionId) || bound,
      value: bound,
    })
  }

  return undefined
}

function asGroupItems(value: AnswerValue | undefined): GroupItem[] {
  return Array.isArray(value) ? value : []
}

export function isGroupItemEmpty(questions: ScalarQuestion[], item: GroupItem): boolean {
  return questions
    .filter((question) => isQuestionVisible(question, item))
    .every((question) => {
      const value = item[question.id]
      if (question.type === 'boolean') return value !== true
      return isEmpty(value)
    })
}

export function validateScreen(
  screen: QuestionsScreen,
  values: Answers,
  texts: ValidationTexts,
  language = 'en'
): Record<string, string> {
  const errors: Record<string, string> = {}
  const visible = screen.questions.filter((q) => isQuestionVisible(q, values))
  const labelFor = (questionId: string) =>
    resolveLabel(findQuestion(screen, questionId)?.label, language)

  for (const question of visible) {
    if (isGroupQuestion(question)) {
      const items = asGroupItems(values[question.id])
      const filledItems = items.filter((item) => !isGroupItemEmpty(question.questions, item))
      const min = groupMinItems(question)
      const max = groupMaxItems(question)

      if (filledItems.length < min) {
        errors[question.id] = format(texts.minItems, { minItems: min })
      } else if (filledItems.length > max) {
        errors[question.id] = format(texts.maxItems, { maxItems: max })
      }

      items.forEach((item, index) => {
        if (isGroupItemEmpty(question.questions, item)) return
        for (const inner of question.questions) {
          const error = validateScalar(inner, item[inner.id], item, texts, labelFor)
          if (error) errors[groupFieldErrorKey(question.id, index, inner.id)] = error
        }
      })
      continue
    }

    const error = validateScalar(question, values[question.id], values, texts, labelFor)
    if (error) errors[question.id] = error
  }

  return errors
}

function buildItemAnswers(questions: ScalarQuestion[], item: GroupItem): GroupItem {
  const result: GroupItem = {}
  for (const question of questions) {
    if (!isQuestionVisible(question, item)) continue
    const value = item[question.id]
    if (isEmpty(value) && question.type !== 'boolean') continue
    result[question.id] = coerceScalar(question, value)
  }
  return result
}

function coerceScalar(
  question: ScalarQuestion,
  value: ScalarAnswerValue | undefined
): ScalarAnswerValue {
  if (question.type === 'boolean') return value === true
  if (question.type === 'number') return typeof value === 'number' ? value : Number(value)
  return value as ScalarAnswerValue
}

export function buildSubmitAnswers(screen: QuestionsScreen, values: Answers): Answers {
  const answers: Answers = {}
  const visible = screen.questions.filter((q) => isQuestionVisible(q, values))

  for (const question of visible) {
    if (isGroupQuestion(question)) {
      const items = asGroupItems(values[question.id])
      answers[question.id] = items
        .filter((item) => !isGroupItemEmpty(question.questions, item))
        .map((item) => buildItemAnswers(question.questions, item))
      continue
    }

    const value = values[question.id]
    if (isEmpty(value) && question.type !== 'boolean') continue
    answers[question.id] = coerceScalar(question, value as ScalarAnswerValue | undefined)
  }

  return answers
}

export function buildInitialValues(
  screen: QuestionsScreen,
  savedAnswers: Answers,
  derivedIds: ReadonlySet<string> = new Set()
): Answers {
  const values: Answers = {}
  for (const question of screen.questions) {
    if (isGroupQuestion(question)) {
      const saved = savedAnswers[question.id]
      if (Array.isArray(saved)) {
        values[question.id] = saved.map((item) => buildItemInitialValues(question.questions, item))
      } else {
        const initial = groupInitialItems(question)
        values[question.id] = Array.from({ length: initial }, () =>
          buildItemInitialValues(question.questions)
        )
      }
      continue
    }

    const saved = savedAnswers[question.id]
    if (saved !== undefined) {
      values[question.id] = saved
    } else if (question.type === 'boolean') {
      values[question.id] = false
    }
  }

  // Seed `defaultFrom` suggestions for fields without an answer, and refresh fields whose saved
  // value is known to be computed (`derivedIds`) — their sources may have changed on another
  // screen since. Saved values the user typed (or of unknown origin, e.g. after a reload) win.
  // Iterates to a fixed point so a suggestion feeding another suggestion is picked up regardless
  // of declaration order.
  const targets = screen.questions.filter(isDefaultFromTarget)
  // Values this routine seeds stay refreshable in later passes (they are derived by definition).
  const seeded = new Set<string>()
  for (let pass = 0; pass < targets.length; pass++) {
    let changed = false
    for (const question of targets) {
      if (question.applyDefaultLabel) continue
      const current = values[question.id]
      const refreshable =
        current === undefined || derivedIds.has(question.id) || seeded.has(question.id)
      if (!refreshable) continue
      const resolved = { ...savedAnswers, ...values }
      if (!hasDefaultFromInput(question.defaultFrom, resolved)) continue
      const computed = computeDefaultFrom(question, resolved)
      if (computed === undefined || sameNumber(current, computed)) continue
      values[question.id] = computed
      seeded.add(question.id)
      changed = true
    }
    if (!changed) break
  }

  return values
}

export function buildItemInitialValues(questions: ScalarQuestion[], saved?: GroupItem): GroupItem {
  const item: GroupItem = {}
  for (const question of questions) {
    const value = saved?.[question.id]
    if (value !== undefined) {
      item[question.id] = value
    } else if (question.type === 'boolean') {
      item[question.id] = false
    }
  }
  return item
}

export const SLOT_MIN_FILES_DEFAULT = 1
export const SLOT_MAX_FILES_DEFAULT = 10
/**
 * Backend default for the presigned path (`defaultUploadMaxSizeMb`), which every file above
 * `PRESIGNED_UPLOAD_THRESHOLD_MB` takes; smaller files stay well under the base64 default (6).
 */
export const SLOT_MAX_SIZE_MB_DEFAULT = 20

/** Files a slot needs before the screen can be submitted: 0 for optional slots, else `minFiles` (default 1). */
export function slotMinFiles(request: DocumentRequest): number {
  if (request.required === false) return 0
  return request.minFiles ?? SLOT_MIN_FILES_DEFAULT
}

export function slotMaxFiles(request: DocumentRequest): number {
  return request.maxFiles ?? SLOT_MAX_FILES_DEFAULT
}

export function slotMaxSizeMb(request: DocumentRequest): number {
  return request.maxSizeMb ?? SLOT_MAX_SIZE_MB_DEFAULT
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPG',
  'image/png': 'PNG',
  'image/heic': 'HEIC',
  'image/heif': 'HEIF',
}

/** Human-readable, de-duplicated list of accepted formats, e.g. "JPEG, PNG, PDF". */
export function formatContentTypes(contentTypes: string[]): string {
  const labels: string[] = []
  for (const type of contentTypes) {
    const label = CONTENT_TYPE_LABELS[type] ?? (type.split('/')[1] || type).toUpperCase()
    if (!labels.includes(label)) labels.push(label)
  }
  return labels.join(', ')
}

export function documentsForRequest(
  documents: UploadedDocument[],
  documentRequestId: string
): UploadedDocument[] {
  return documents.filter((doc) => doc.documentRequestId === documentRequestId)
}

export function validateUploads(
  documentRequests: DocumentRequest[],
  documents: UploadedDocument[],
  requiredText: string
): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const request of documentRequests) {
    const min = slotMinFiles(request)
    if (documentsForRequest(documents, request.id).length < min) {
      errors[request.id] = format(requiredText, { minFiles: min })
    }
  }
  return errors
}
